import React from 'react';
import { ITrainingSession } from '../../../types/training-cycle/training-session';
import { contactMethodOptions, sessionModeOptions } from '../../../types/coach/dropdown-options';
import { PD, SF } from './tokens';
import { formatSessionTime } from './helpers';

interface MetaStripProps {
  session?: Partial<ITrainingSession>;
  editable: boolean;
  onEdit?: () => void;
}

interface ModeMeta {
  label: string;
  icon: string;
}

const SESSION_MODE_LABEL: Record<string, ModeMeta> = {
  virtual_asincronico: { label: 'Virtual asíncrono', icon: 'pi-video' },
  virtual_sincronico: { label: 'Virtual sincrónico', icon: 'pi-video' },
  presencial: { label: 'Presencial', icon: 'pi-map-marker' },
  hibrido: { label: 'Híbrido', icon: 'pi-link' }
};

interface ChipProps {
  icon: string;
  children: React.ReactNode;
}

const MetaChip: React.FC<ChipProps> = ({ icon, children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontFamily: SF,
      fontSize: 12.5,
      fontWeight: 500,
      color: PD.ink2,
      whiteSpace: 'nowrap'
    }}
  >
    <i className={`pi ${icon}`} style={{ fontSize: 13, color: PD.ink3 }} />
    {children}
  </span>
);

const MetaSeparator: React.FC = () => (
  <span
    style={{
      width: 3,
      height: 3,
      borderRadius: '50%',
      background: PD.ink3,
      opacity: 0.5,
      flex: '0 0 auto'
    }}
  />
);

function sessionModeMeta(mode?: string): ModeMeta | null {
  if (!mode) return null;
  if (SESSION_MODE_LABEL[mode]) return SESSION_MODE_LABEL[mode];
  // Fallback: try options table for a label, default icon
  const opt = sessionModeOptions.find((o) => o.value === mode);
  if (opt) return { label: opt.label, icon: 'pi-calendar' };
  return null;
}

function contactMethodLabel(method?: string): string {
  if (!method) return '';
  const opt = contactMethodOptions.find((o) => o.value === method);
  return opt?.label || method;
}

const MetaStrip: React.FC<MetaStripProps> = ({ session, editable, onEdit }) => {
  const mode = sessionModeMeta(session?.sessionMode);
  const chips: React.ReactNode[] = [];

  if (mode)
    chips.push(
      <MetaChip key="mode" icon={mode.icon}>
        {mode.label}
      </MetaChip>
    );
  if (session?.sessionTime) {
    const t = formatSessionTime(session.sessionTime);
    if (t)
      chips.push(
        <MetaChip key="time" icon="pi-clock">
          {t}
        </MetaChip>
      );
  }
  if (session?.location)
    chips.push(
      <MetaChip key="loc" icon="pi-map-marker">
        {session.location}
      </MetaChip>
    );
  if (session?.contactMethod) {
    chips.push(
      <MetaChip key="contact" icon="pi-phone">
        {contactMethodLabel(session.contactMethod)}
      </MetaChip>
    );
  }
  if (session?.notes)
    chips.push(
      <MetaChip key="notes" icon="pi-file-edit">
        {session.notes}
      </MetaChip>
    );

  return (
    <div
      style={{
        padding: '10px 18px',
        borderBottom: `0.5px solid ${PD.hairline}`,
        background: PD.card,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flex: '0 0 auto'
      }}
    >
      <div
        className={chips.length > 0 ? 'pd-mask-right pd-no-scrollbar' : 'pd-no-scrollbar'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden'
        }}
      >
        {chips.length === 0 ? (
          <span style={{ fontFamily: SF, fontSize: 12.5, color: PD.ink3 }}>Sin información de sesión</span>
        ) : (
          chips.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <MetaSeparator />}
              {c}
            </React.Fragment>
          ))
        )}
      </div>
      {editable && (
        <button
          type="button"
          onClick={onEdit}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 10px',
            borderRadius: 999,
            background: PD.fill,
            border: 'none',
            fontFamily: SF,
            fontSize: 12,
            fontWeight: 600,
            color: PD.ink2,
            cursor: 'pointer'
          }}
        >
          <i className="pi pi-pencil" style={{ fontSize: 11 }} />
          Editar
        </button>
      )}
    </div>
  );
};

export default MetaStrip;
